import { ErrorHandler, Injectable } from '@angular/core';

const RELOAD_FLAG = 'munch:chunk-reload-at';
const RELOAD_COOLDOWN_MS = 10_000;

function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;
  const message =
    error instanceof Error
      ? `${error.name} ${error.message}`
      : typeof error === 'string'
        ? error
        : String((error as { message?: unknown })?.message ?? '');
  return (
    /ChunkLoadError/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Failed to load module script/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message)
  );
}

// Reload at most once per cooldown window so a chunk that's truly missing
// (rather than stale) can't trap the user in a refresh loop.
function reloadOnce(): void {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_FLAG) ?? '0');
    if (Date.now() - last < RELOAD_COOLDOWN_MS) return;
    sessionStorage.setItem(RELOAD_FLAG, String(Date.now()));
  } catch {
    // sessionStorage unavailable (private mode etc.) — fall through and reload anyway
  }
  location.reload();
}

@Injectable()
export class ChunkLoadErrorHandler implements ErrorHandler {
  handleError(error: unknown): void {
    if (isChunkLoadError(error)) {
      reloadOnce();
      return;
    }
    console.error(error);
  }
}

export function installGlobalChunkReloadListeners(): void {
  if (typeof window === 'undefined') return;
  window.addEventListener('unhandledrejection', (event) => {
    if (isChunkLoadError(event.reason)) reloadOnce();
  });
  window.addEventListener('error', (event) => {
    if (isChunkLoadError(event.error) || isChunkLoadError(event.message)) reloadOnce();
  });
}
